import { useMemo, useState } from 'react';
import { Coins, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { EmptyState } from '../common/EmptyState';
import { SectionSkeleton } from '../common/SectionSkeleton';
import { ExpensesSectionLabel } from '../expenses/ExpensesSectionLabel';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import {
  useDeleteIncomeRecordMutation,
  useIncomeRecordsQuery,
  useIncomeSourcesQuery,
} from '../../features/income/queries';
import { extractMutationError } from '../../utils/apiError';
import { groupRecordsByMonth } from '../../lib/incomeCycleUtils';
import { formatEUR } from '../../utils/money';
import { QuickIncomeSheet } from './QuickIncomeSheet';
import { IncomeSourceIcon } from './IncomeSourceIcon';

/** `.pen` → `ds/card/record-day-group` › `rows` › `row-*` (variante rendimento:
 *  valores positivos em `$sage-on-tint`, meta = data curta). */
function IncomeRow({ record, source, canEdit, onEdit, onDelete }) {
  return (
    <li className="flex min-w-0 items-center gap-space-3 border-b border-border px-space-4 py-space-2-5 last:border-b-0">
      <IncomeSourceIcon
        source={source}
        size="md"
        background="muted"
        className="rounded-ds-md"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="type-body truncate font-semibold text-card-foreground">
          {record.source_name}
        </p>
        {record.notes ? (
          <p className="type-chip truncate font-normal text-muted-foreground">
            {record.notes}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <p className="type-display-row tnum text-sage-on-tint">
          {`+ ${formatEUR(Number(record.amount))}`}
        </p>
        <p className="type-chip tnum font-normal text-muted-foreground">
          {record.shortDate}
        </p>
      </div>

      {canEdit ? (
        <div className="flex shrink-0 items-center gap-space-1">
          <button
            type="button"
            onClick={() => onEdit(record)}
            className="rounded-ds-full p-1 text-muted-foreground transition-colors hover:bg-muted"
            aria-label={pt.income.list.edit}
          >
            <PencilSimple className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onDelete(record)}
            className="rounded-ds-full p-1 text-muted-foreground transition-colors hover:bg-danger-tint hover:text-danger-fg"
            aria-label={pt.income.list.delete}
          >
            <Trash className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** `.pen` → `ds/card/record-day-group` (agrupado por mês civil em Rendimentos). */
function MonthCard({ group, sourceById, canEdit, onEdit, onDelete }) {
  return (
    <section
      data-ds-id="ds/card/record-day-group"
      className="flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border border-border bg-card shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]"
    >
      <div className="flex min-w-0 items-center justify-between gap-space-3 border-b border-border px-space-4 py-space-3">
        <h3 className="type-body-sm font-display font-bold capitalize text-card-foreground">
          {group.label}
        </h3>
        <p className="type-body-sm tnum font-display font-bold text-sage-on-tint">
          {formatEUR(group.total)}
        </p>
      </div>

      <ul className="flex min-w-0 flex-col">
        {group.items.map((record) => (
          <IncomeRow
            key={record.id}
            record={record}
            source={
              sourceById.get(String(record.source)) || {
                category: record.source_category,
                name: record.source_name,
              }
            }
            canEdit={canEdit}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </section>
  );
}

export function IncomeRecordsList({
  cycle,
  canEdit = true,
  onRegisterClick,
  cycles = [],
  cycleIndex = 0,
  onCycleIndexChange,
  showSubtitle = true,
}) {
  const filters = useMemo(
    () =>
      cycle
        ? {
            received_on_from: cycle.start,
            received_on_to: cycle.end,
          }
        : null,
    [cycle],
  );

  const {
    data: records,
    isPending,
    isFetching,
    isPlaceholderData,
  } = useIncomeRecordsQuery(filters, { enabled: Boolean(filters) });
  const recordsShell = useQueryShellState({
    data: records,
    isPending,
    isFetching,
    isPlaceholderData: Boolean(filters) && Boolean(isPlaceholderData),
  });
  const recordsList = useMemo(() => records ?? [], [records]);
  const { data: sources = [] } = useIncomeSourcesQuery({
    enabled: Boolean(filters),
  });
  const deleteRecord = useDeleteIncomeRecordMutation() ?? {
    mutateAsync: async () => {},
    isPending: false,
  };

  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const monthGroups = useMemo(() => groupRecordsByMonth(recordsList), [recordsList]);
  const sourceById = useMemo(() => {
    const map = new Map();
    sources.forEach((source) => map.set(String(source.id), source));
    return map;
  }, [sources]);

  const canGoPrevCycle = cycleIndex < cycles.length - 1;

  const onConfirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteRecord.mutateAsync(deleting.id);
      toast.success(pt.income.list.deletedToast);
      setDeleting(null);
    } catch (err) {
      toast.error(extractMutationError(err, pt.income.list.deleteError));
    }
  };

  if (recordsShell.showBlockingSkeleton) {
    return <SectionSkeleton variant="list" />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-space-5">
      <ExpensesSectionLabel
        title={pt.income.cycleRecords.title}
        hint={showSubtitle ? pt.income.cycleRecords.subtitle : null}
      />

      {recordsList.length === 0 ? (
        <EmptyState
          icon={Coins}
          title={pt.income.list.emptyTitle}
          description={pt.income.list.empty}
          action={
            canEdit && onRegisterClick ? (
              <Button type="button" onClick={onRegisterClick}>
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                {pt.income.cta.quickRegister}
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="flex min-w-0 flex-col gap-space-2">
            {monthGroups.map((group) => (
              <MonthCard
                key={group.monthKey}
                group={group}
                sourceById={sourceById}
                canEdit={canEdit}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            ))}
          </div>

          {canGoPrevCycle && onCycleIndexChange ? (
            <div className="flex justify-center pt-space-1">
              <button
                type="button"
                className="type-link-sm text-sage-on-tint hover:underline"
                onClick={() =>
                  onCycleIndexChange(Math.min(cycles.length - 1, cycleIndex + 1))
                }
              >
                {pt.income.cycleRecords.seeHistory}
              </button>
            </div>
          ) : null}
        </>
      )}

      <QuickIncomeSheet
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        cycle={cycle}
        editing={editing}
      />

      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.income.list.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>
              {pt.income.list.deleteConfirmBody.replace(
                '{source}',
                deleting?.source_name || '',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-space-2">
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              {pt.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteRecord.isPending}
            >
              {deleteRecord.isPending ? pt.common.loading : pt.income.list.deleteConfirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
